import { Injectable, inject } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

declare global {
  interface Window {
    dataLayer?: Record<string, unknown>[];
  }
}

/**
 * Analítica de producto hacia GA4 vía Google Tag Manager (guía GrowData
 * "GA4 + GTM en Angular", contenedor del FNA).
 *
 * - Complementa (NO reemplaza) a AnalyticsTrackerService, que reporta al gateway.
 * - En vez del snippet estático en index.html, el script de GTM se inyecta aquí
 *   leyendo `environment.gtmId`: un solo index.html sirve para todos los
 *   ambientes y con gtmId vacío el servicio es no-op (dev no contamina datos).
 * - Al ser una SPA, GA4 no ve la navegación interna: este servicio empuja un
 *   `page_view` al dataLayer en cada NavigationEnd (GTM lo convierte en tag GA4).
 */
@Injectable({ providedIn: 'root' })
export class GtmAnalyticsService {
  private router = inject(Router);
  private initialized = false;

  private get gtmId(): string {
    return (environment as { gtmId?: string }).gtmId ?? '';
  }

  init(): void {
    if (!this.gtmId || this.initialized) {
      return;
    }
    this.initialized = true;
    this.injectGtmScript(this.gtmId);
    this.trackPagesOnNavigation();
  }

  /** Evento de negocio (abrir_pasarela_pago, iniciar_solicitud, ...). No-op sin GTM. */
  trackEvent(eventName: string, params: Record<string, unknown> = {}): void {
    if (!this.gtmId) {
      return;
    }
    this.pushToDataLayer({ event: eventName, ...params });
  }

  /** Equivalente programático del snippet oficial de GTM (head). */
  private injectGtmScript(gtmId: string): void {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtm.js?id=${gtmId}`;
    document.head.appendChild(script);
  }

  /** page_view por cada navegación de la SPA (GA4 no detecta rutas internas solo). */
  private trackPagesOnNavigation(): void {
    this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe(event => {
        this.pushToDataLayer({
          event: 'page_view',
          page_path: event.urlAfterRedirects,
          page_title: document.title,
        });
      });
  }

  private pushToDataLayer(payload: Record<string, unknown>): void {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(payload);
  }
}
